/* Build Your Bundle — Rebuy SmartCart drawer fold.

   Rebuy SmartCart's "Nested Cart Items" feature only fires for bundles
   produced by Shopify's Cart Transform Function (Rebuy Bundle Builder /
   Shopify-native customizable bundles). Stay.ai's BYOB convention is
   property-based — a shared bundle_id on every line, bundle_parent:true on the
   parent — which Rebuy does NOT recognize natively. Without this shim the
   drawer renders parent + N children as N+1 flat rows.

   This script post-processes the rendered drawer, reading everything from the
   rendered rows (NOT /cart.js — Rebuy does not guarantee DOM row order matches
   the cart). It:
     - Groups rows by Rebuy's per-row `property-…bundle_id:<token>` class, used
       purely as an opaque grouping label (see rowBundleId — the real,
       hyphenated bundle_id is never altered anywhere).
     - Hides child rows and the parent's visible property list (so the leaked
       bundle_id / bundle_parent text disappears), then injects a children list
       + combined total into the parent row.
     - Intercepts the parent row's trash-icon click so removing the parent also
       removes its children (otherwise children orphan with a $0 parent gone).

   Works whether props are unprefixed (bundle_id) or prefixed (_bundle_id): the
   grouping class and the bundle_parent:true class exist in both cases. @stayai

   Reapplies on Rebuy events + a MutationObserver on the scroll area, so Vue
   re-renders don't strip our changes. */
(function () {
  'use strict';

  var ROW_SEL = '.rebuy-cart__flyout-item';
  // The flyout items are unclassed <li>s inside the scroll area; there is NO
  // `.rebuy-cart__flyout-items` container (confirmed live 2026-07-07). Observe
  // the scroll area and query rows globally instead. @stayai
  var SCROLL_SEL = '[data-rebuy-cart-scroll-area]';
  var observer = null;
  var rafScheduled = null;

  // --- Row identity (from Rebuy's own per-row CSS classes) -------------------
  // Rebuy bakes each line property into classes, e.g.
  //   property-bundle_id:124a4cc1d28e…   (hyphens stripped, lowercased)
  //   property-bundle_parent:true
  // The bundle_id token groups rows against each other (string equality only);
  // it is never written back or treated as the canonical id. @stayai
  function rowBundleId(row) {
    var m = row.className.match(/property-_?bundle_id:([a-z0-9]+)/);
    return m ? m[1] : null;
  }
  function rowIsParent(row) {
    return row.className.indexOf('bundle_parent:true') !== -1;
  }
  // Strip hyphens + lowercase — reproduces Rebuy's class-token form of a hex
  // UUID. Used ONLY as a read-only match in hookParentRemove to find which
  // /cart.js line keys belong to the clicked bundle. Nothing is written. @stayai
  function normId(id) {
    return String(id == null ? '' : id).replace(/-/g, '').toLowerCase();
  }
  function getProp(item, key) {
    var props = item && item.properties;
    if (!props) return null;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        var p = props[i];
        if (p && (p.name === key || p.key === key)) {
          return p.value !== undefined ? p.value : p[1];
        }
      }
      return null;
    }
    return props[key] != null ? props[key] : null;
  }
  function itemBundleId(item) {
    return getProp(item, 'bundle_id') || getProp(item, '_bundle_id');
  }

  // --- Money -----------------------------------------------------------------
  function parseCents(text) {
    var money = String(text || '').match(/[$£€]\s?[\d,]+(?:\.\d+)?/);
    if (!money) return 0;
    var n = parseFloat(money[0].replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents || 0); } catch (e) { /* fall through */ }
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' })
        .format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  // --- Scrape one child row's display data -----------------------------------
  function scrapeChild(row) {
    var img = row.querySelector('.rebuy-cart__flyout-item-media img');
    var titleEl = row.querySelector('.rebuy-cart__flyout-item-product-title');
    var variantEl = row.querySelector('.rebuy-cart__flyout-item-variant-title');
    // Current (post-discount) price: prefer the sale node, else the plain money
    // node (skip the struck-through compare-at).
    var priceEl = row.querySelector('.rebuy-cart__flyout-item-price .rebuy-money.sale')
      || row.querySelector('.rebuy-cart__flyout-item-price .rebuy-money:not(.compare-at)');
    var qtyLabel = row.querySelector('.rebuy-cart__flyout-item-quantity-widget-label');
    var qty = '';
    if (qtyLabel) {
      qty = qtyLabel.textContent || '';
      var sr = qtyLabel.querySelector('.sr-only');
      if (sr) qty = qty.replace(sr.textContent, '');
      qty = qty.trim();
    }
    var priceText = priceEl ? priceEl.textContent : '';
    var moneyMatch = String(priceText).match(/[$£€]\s?[\d,]+(?:\.\d+)?/);
    return {
      imgSrc: img ? img.getAttribute('src') : '',
      imgAlt: img ? (img.getAttribute('alt') || '') : '',
      title: titleEl ? titleEl.textContent.trim() : '',
      variant: variantEl ? variantEl.textContent.trim() : '',
      qty: qty || '1',
      priceText: moneyMatch ? moneyMatch[0].replace(/\s/g, '') : '',
      cents: parseCents(priceText)
    };
  }

  function buildChildrenList(children) {
    var ul = document.createElement('ul');
    ul.className = 'byob-bundle-children';
    children.forEach(function (c) {
      var li = document.createElement('li');
      li.className = 'byob-bundle-children__item';

      var img = document.createElement('img');
      img.className = 'byob-bundle-children__image';
      img.src = c.imgSrc || '';
      img.alt = c.imgAlt || c.title || '';
      img.loading = 'lazy';
      img.width = 40;
      img.height = 40;
      li.appendChild(img);

      var info = document.createElement('div');
      info.className = 'byob-bundle-children__info';

      var title = document.createElement('span');
      title.className = 'byob-bundle-children__title';
      title.textContent = c.title || '';
      info.appendChild(title);

      if (c.variant && c.variant !== 'Default Title') {
        var variant = document.createElement('span');
        variant.className = 'byob-bundle-children__variant';
        variant.textContent = c.variant;
        info.appendChild(variant);
      }

      var qty = document.createElement('span');
      qty.className = 'byob-bundle-children__qty';
      qty.textContent = 'Qty ' + (c.qty || '1');
      info.appendChild(qty);

      li.appendChild(info);
      ul.appendChild(li);
    });
    return ul;
  }

  function setParentPrice(parentRow, totalCents) {
    var priceEl = parentRow.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceEl) return;
    if (priceEl.dataset.byobBundleTotal === String(totalCents)) return;
    priceEl.dataset.byobBundleTotal = String(totalCents);
    priceEl.textContent = formatMoney(totalCents);
  }

  function applyFold() {
    var rows = Array.prototype.slice.call(document.querySelectorAll(ROW_SEL));
    if (rows.length === 0) return;

    // Group rows by their opaque bundle_id class token. Rows with no bundle_id
    // class are standalone lines — left untouched.
    var groups = {};
    rows.forEach(function (row) {
      var token = rowBundleId(row);
      if (!token) return;
      if (!groups[token]) groups[token] = { parentRow: null, childRows: [] };
      if (rowIsParent(row)) groups[token].parentRow = row;
      else groups[token].childRows.push(row);
    });

    Object.keys(groups).forEach(function (token) {
      var g = groups[token];
      if (!g.parentRow || g.childRows.length === 0) return;

      var parentRow = g.parentRow;
      parentRow.dataset.byobRole = 'parent';
      parentRow.dataset.byobBundleId = token;
      parentRow.classList.add('byob-bundle-parent');

      var children = [];
      var totalCents = 0; // parent line is a $0 placeholder, so children == total
      g.childRows.forEach(function (row) {
        row.dataset.byobRole = 'child';
        row.dataset.byobBundleId = token;
        row.style.display = 'none';
        var c = scrapeChild(row);
        children.push(c);
        totalCents += c.cents;
      });

      var infoSection = parentRow.querySelector('.rebuy-cart__flyout-item-info') || parentRow;

      // Hide the parent's visible property list (the leaked bundle_id /
      // bundle_parent text). Under _-prefixed props Rebuy won't render it at
      // all, so this is a no-op then.
      var propsBlock = parentRow.querySelector('.rebuy-cart__flyout-item-properties');
      if (propsBlock) propsBlock.style.display = 'none';

      // Frequency label — from the parent's subscription <select> when it's on
      // a plan (not one-time). Idempotent: replace only if the text changed.
      var sel = parentRow.querySelector('.rebuy-cart__flyout-item-subscription select');
      var planName = (sel && sel.value && sel.value !== 'onetime' && sel.options[sel.selectedIndex])
        ? sel.options[sel.selectedIndex].text.replace(/\*+\s*$/, '').trim()
        : null;
      var existingFreq = infoSection.querySelector(':scope > .byob-bundle-frequency');
      if (planName) {
        var freqText = 'Subscribe & Save · ' + planName;
        if (!existingFreq || existingFreq.textContent !== freqText) {
          if (existingFreq) existingFreq.remove();
          var freq = document.createElement('div');
          freq.className = 'byob-bundle-frequency';
          freq.textContent = freqText;
          var titleEl = infoSection.querySelector('.rebuy-cart__flyout-item-product-title');
          if (titleEl && titleEl.nextSibling) {
            titleEl.parentNode.insertBefore(freq, titleEl.nextSibling);
          } else {
            infoSection.insertBefore(freq, infoSection.firstChild);
          }
        }
      } else if (existingFreq) {
        existingFreq.remove();
      }

      setParentPrice(parentRow, totalCents);

      // Idempotent children-list injection — skip if unchanged so our own
      // mutations don't cause churn on the observer re-fire.
      var fingerprint = children
        .map(function (c) { return c.title + '|' + c.variant + '|' + c.qty + '|' + c.priceText; })
        .join('||');
      var existing = parentRow.querySelector(':scope > .rebuy-cart__flyout-item-info > .byob-bundle-children, :scope > .byob-bundle-children');
      if (existing && existing.dataset.byobFingerprint === fingerprint) return;
      if (existing) existing.parentNode.removeChild(existing);

      var ul = buildChildrenList(children);
      ul.dataset.byobFingerprint = fingerprint;
      infoSection.appendChild(ul);
    });
  }

  function refresh() {
    rafScheduled = null;
    applyFold();
  }

  function schedule() {
    if (rafScheduled !== null) return;
    rafScheduled = (window.requestAnimationFrame || window.setTimeout)(refresh, 16);
  }

  function attachObserver() {
    var target = document.querySelector(SCROLL_SEL)
      || (document.querySelector(ROW_SEL) && document.querySelector(ROW_SEL).parentNode);
    if (!target) return false;
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () { schedule(); });
    observer.observe(target, { childList: true, subtree: true });
    return true;
  }

  function attachListeners() {
    [
      'rebuy:smartcart.ready',
      'rebuy:smartcart.show',
      'rebuy:cart.change',
      'rebuy:smartcart.line-item-increase',
      'rebuy:smartcart.line-item-decrease',
      'rebuy:smartcart.line-item-removed',
      'rebuy:cart.add'
    ].forEach(function (name) {
      document.addEventListener(name, schedule);
    });

    if (!attachObserver()) {
      var tries = 0;
      var iv = window.setInterval(function () {
        tries++;
        if (attachObserver() || tries > 50) window.clearInterval(iv);
      }, 200);
    }
  }

  /* Capture-phase click handler on the parent row's trash button.
     Without this, Rebuy removes only the parent line; the children would
     orphan in the cart with a missing $0 parent. We pre-empt Rebuy's per-line
     removeItem and remove every bundle line at once via /cart/update.js.
     Rows expose no line-item key, so we fetch /cart.js for the keys and match
     by bundle_id (normId reproduces Rebuy's token form — read-only). @stayai */
  function hookParentRemove() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.rebuy-cart__flyout-item-remove');
      if (!btn) return;
      var row = btn.closest(ROW_SEL);
      if (!row || row.dataset.byobRole !== 'parent') return;
      var token = row.dataset.byobBundleId;
      if (!token) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var updates = {};
          cart.items.forEach(function (item) {
            if (normId(itemBundleId(item)) === token) updates[item.key] = 0;
          });
          return fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
        })
        .then(function () {
          if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.refresh === 'function') {
            window.Rebuy.SmartCart.refresh();
          }
          schedule();
        })
        .catch(function () { /* on error, fall back to default behavior on next click */ });
    }, true);
  }

  function init() {
    attachListeners();
    hookParentRemove();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
